import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import useIsAllowed from "../../../auth/accessToken/hooks/useIsAllowed";
import { useAccountId } from "../../../auth/useAccountId";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type { SalesLocation } from "../../../types/customers";

const fetchLocation = async (payload: {
    locationId: string;
    accountId: string;
}): Promise<SalesLocation> => {
    return await fulfill.get({
        accountId: payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${payload.accountId}/locations/${payload.locationId}`,
        handlers: {
            200: (data) => data,
            404: () => null,
        },
    });
};

const fetchDeleteLocation = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            locationId: string;
        };
    },
): Promise<SalesLocation> => {
    return await fulfill.delete({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/locations/${options.arg.locationId}`,
        handlers: {
            200: (data) => data,
        },
    });
};

const fetchPutLocation = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            locationId: string;
            location: Partial<SalesLocation>;
        };
    },
): Promise<SalesLocation> => {
    return await fulfill.put<Partial<SalesLocation>>({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/locations/${options.arg.locationId}`,
        json: options.arg.location,
        handlers: {
            200: (data) => data,
        },
    });
};

export const useLocation = (locationId: string) => {
    const canAccessLocations = useIsAllowed([
        ["admin:accounts"],
        ["admin:locations"],
        ["read:locations"],
    ]);
    const accountId = useAccountId();
    const { data, isLoading, error } = useSWR(
        () =>
            accountId && locationId && canAccessLocations
                ? {
                      key: `${accountId}/locations/${locationId}`,
                      accountId,
                      locationId,
                  }
                : null,
        fetchLocation,
    );
    const {
        trigger: deleteLocation,
        isMutating: isDeletingLocation,
        error: deleteLocationError,
    } = useSWRMutation(
        () =>
            canAccessLocations ? `${accountId}/locations/${locationId}` : null,
        fetchDeleteLocation,
    );
    const {
        trigger: updateLocation,
        isMutating: isUpdatingLocation,
        error: updateLocationError,
    } = useSWRMutation(
        () =>
            canAccessLocations ? `${accountId}/locations/${locationId}` : null,
        fetchPutLocation,
    );

    return {
        location: data,
        isLoading,
        error,
        deleteLocation,
        isDeletingLocation,
        deleteLocationError,
        updateLocation,
        isUpdatingLocation,
        updateLocationError,
    };
};
